import axios from 'axios';

const api = axios.create({
  baseURL: '/api',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add auth token to requests if available
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('proparv_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('proparv_token');
      localStorage.removeItem('proparv_user');
    }
    return Promise.reject(error);
  }
);

// Property API
export async function searchProperty(address) {
  const response = await api.post('/property/search', { address }, { timeout: 60000 });
  return response.data;
}

export async function getStreetViewUrl(address) {
  const response = await api.get('/property/street-view', { params: { address } });
  return response.data;
}

export async function getComps(city, state, beds, baths) {
  const response = await api.get('/property/comps', { params: { city, state, beds, baths } });
  return response.data;
}

export async function getCompsWithFilters(address, sqft, beds, baths, filters = {}) {
  const response = await api.post('/property/comps-arv', {
    address, sqft, beds, baths,
    maxDistanceMi:   filters.maxDistanceMi   || 1.0,
    timeframeMonths: filters.timeframeMonths || 12,
    status:          filters.status          || 'sold',
    propertyType:    filters.propertyType    || null
  });
  return response.data;
}

export async function getSubscriptionStatus() {
  const response = await api.get('/subscription/status');
  return response.data;
}

export async function startFreeTrial() {
  const response = await api.post('/subscription/start-trial');
  return response.data;
}

// AI API
export async function getAIInsight(dealData) {
  const response = await api.post('/ai/analyze', dealData);
  return response.data;
}

export async function getRehabEstimate(sqft, location, items) {
  const response = await api.post('/ai/rehab-estimate', { sqft, location, items });
  return response.data;
}

// AI Valuation — web search for sold comps, ARV, rehab, ROI (rent is separate)
export async function getAIValuation(address, { listPrice, beds, baths, sqft, yearBuilt, propertyType, strategy } = {}) {
  const response = await api.post('/ai/valuation', {
    address, listPrice, beds, baths, sqft, yearBuilt, propertyType, strategy
  }, { timeout: 90000 });
  return response.data;
}

// AI Rent — dedicated rental comp search for accurate monthly rent range
export async function getAIRent(address, beds, baths) {
  const response = await api.post('/ai/rent', { address, beds, baths });
  return response.data;
}

// Auth API
export async function loginUser(email, password) {
  const response = await api.post('/auth/login', { email, password });
  return response.data;
}

export async function registerUser(name, email, password) {
  const response = await api.post('/auth/register', { name, email, password });
  return response.data;
}

export async function logoutUser() {
  try {
    await api.post('/auth/logout');
  } catch (e) {
    // Silent fail
  }
  localStorage.removeItem('proparv_token');
  localStorage.removeItem('proparv_user');
}

export default api;
